import { FC, Fragment, useEffect, useMemo, useState } from 'react';
import { OrderItem, OrderResponse } from '../../../api/orders';
import { apiFetch } from '../../../utils/api';
import { UserProfile } from '../../../api/auth';
import SearchableDropdown, { SearchableOption } from '../../../components/SearchableDropdown';
import { fetchUsers } from '../../../api/users';
import { getUserDisplayName } from '../../../utils/users';
import { getOrderItemDisplayName, isServiceOrderItem } from '../../../utils/orders';
import { ServiceAssignmentValue } from '../hooks/useServiceAssignments';

interface ServiceAssignmentsTableProps {
  order: OrderResponse | null;
  assignments: Record<string, ServiceAssignmentValue>;
  onChange?: (orderItemId: string, technician: UserProfile | null) => void;
  onBayChange?: (orderItemId: string, bayId: string | null) => void;
  onCompletedChange?: (orderItemId: string, completed: boolean) => void;
  onNotesChange?: (orderItemId: string, notes: string) => void;
  onStartTimeChange?: (orderItemId: string, startTime: string | null) => void;
  onEndTimeChange?: (orderItemId: string, endTime: string | null) => void;
  onNextDueChange?: (orderItemId: string, days: string) => void;
  nextDueByItem?: Record<string, string>;
  disabled?: boolean;
  readOnly?: boolean;
  allowStatusEditing?: boolean;
  allowNotesEditing?: boolean;
  allowNextDueEditing?: boolean;
}

const ServiceAssignmentsTable: FC<ServiceAssignmentsTableProps> = ({
  order,
  assignments,
  onChange,
  onCompletedChange,
  onBayChange,
  onNotesChange,
  onStartTimeChange,
  onEndTimeChange,
  onNextDueChange,
  nextDueByItem,
  disabled = false,
  readOnly = false,
  allowStatusEditing = false,
  allowNotesEditing = false,
  allowNextDueEditing = false,
}) => {
  const [bays, setBays] = useState<Array<{ id: string; name: string }>>([]);
  useEffect(() => {
    let ignore = false;
    apiFetch<{ data: any[] }>('/bays')
      .then((resp) => {
        if (ignore) return;
        const arr = Array.isArray(resp?.data) ? resp.data : [];
        // Normalize ids to strings to keep <select> value matching stable
        setBays(arr.map((b: any) => ({ id: String(b.id), name: b.name })));
      })
      .catch(() => { if (!ignore) setBays([]); });
    return () => { ignore = true; };
  }, []);
  const serviceItems = useMemo<OrderItem[]>(
    () => (order?.items ?? []).filter(isServiceOrderItem),
    [order],
  );

  const [activeItemId, setActiveItemId] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [searchResults, setSearchResults] = useState<UserProfile[]>([]);
  const [searching, setSearching] = useState(false);
  const [currentTimeConflicts, setCurrentTimeConflicts] = useState<any[]>([]);
  const [dayConflicts, setDayConflicts] = useState<any[]>([]);
  const [bayDayConflicts, setBayDayConflicts] = useState<any[]>([]);
  const [pendingAssignment, setPendingAssignment] = useState<ServiceAssignmentValue | null>(null);
  const [localError, setLocalError] = useState<string | null>(null);

  // Helper: build full-day ISO window for availability queries
  const buildDayWindow = (referenceIso?: string | null) => {
    // Priority: 1. Manual selection, 2. Order appointment date, 3. Today
    const refDate = referenceIso || order?.appointment?.scheduledAt || new Date().toISOString();
    const ref = new Date(refDate);
    const start = new Date(ref); start.setHours(10, 0, 0, 0);
    const end = new Date(ref); end.setHours(18, 0, 0, 0);
    return { dayStart: start.toISOString(), dayEnd: end.toISOString() };
  };

  // Fetch technician's full-day conflicts whenever technician selection changes
  useEffect(() => {
    const techId = pendingAssignment?.technician?.id;
    if (!activeItemId || !techId) { setDayConflicts([]); return; }
    let isMounted = true;
    const { dayStart, dayEnd } = buildDayWindow(pendingAssignment?.startTime);
    const url = `/orders/service-assignments/availability?technicianId=${encodeURIComponent(techId)}&startTime=${encodeURIComponent(dayStart)}&endTime=${encodeURIComponent(dayEnd)}`;
    apiFetch<any[]>(url)
      .then((data) => { if (isMounted) setDayConflicts(data || []); })
      .catch(() => { if (isMounted) setDayConflicts([]); });
    return () => { isMounted = false; };
  }, [activeItemId, pendingAssignment?.technician?.id, pendingAssignment?.startTime]);

  // Fetch bay's full-day conflicts whenever bay selection changes (independent of technician)
  useEffect(() => {
    const bayId = pendingAssignment?.bayId;
    if (!activeItemId || !bayId) { setBayDayConflicts([]); return; }
    let isMounted = true;
    const { dayStart, dayEnd } = buildDayWindow(pendingAssignment?.startTime);
    const url = `/orders/service-assignments/availability?bayId=${encodeURIComponent(bayId)}&startTime=${encodeURIComponent(dayStart)}&endTime=${encodeURIComponent(dayEnd)}`;
    apiFetch<any[]>(url)
      .then((data) => { if (isMounted) setBayDayConflicts(data || []); })
      .catch(() => { if (isMounted) setBayDayConflicts([]); });
    return () => { isMounted = false; };
  }, [activeItemId, pendingAssignment?.bayId, pendingAssignment?.startTime]);

  // Handle active item changes and local state initialization
  useEffect(() => {
    if (activeItemId) {
      const current = assignments[activeItemId];
      if (current) {
        // Initialize pending state with current values
        const initial = { ...current };
        if (!initial.startTime) {
          const d = new Date();
          d.setHours(10, 0, 0, 0);
          initial.startTime = d.toISOString();
        }
        setPendingAssignment(initial);
      }
      setLocalError(null);
    } else {
      setPendingAssignment(null);
      setLocalError(null);
    }
  }, [activeItemId, assignments]);

  // Fetch current window conflicts based on pending times
  useEffect(() => {
    if (!activeItemId || !pendingAssignment?.startTime || !pendingAssignment?.endTime) {
      setCurrentTimeConflicts([]);
      return;
    }

    let isMounted = true;
    const url = `/orders/service-assignments/availability?startTime=${encodeURIComponent(pendingAssignment.startTime)}&endTime=${encodeURIComponent(pendingAssignment.endTime)}&excludeAssignmentId=${encodeURIComponent(pendingAssignment.id || '')}`;

    apiFetch<any[]>(url)
      .then((data) => {
        // Exclude assignments belonging to the same order — they are not external conflicts
        const crossOrderConflicts = (data || []).filter(
          (c: any) => String(c.orderId) !== String(order?.id),
        );
        if (isMounted) setCurrentTimeConflicts(crossOrderConflicts);
      })
      .catch((err) => {
        console.error('Failed to fetch availability', err);
        if (isMounted) setCurrentTimeConflicts([]);
      });

    return () => { isMounted = false; };
  }, [activeItemId, pendingAssignment?.startTime, pendingAssignment?.endTime, pendingAssignment?.id]);

  const [searchError, setSearchError] = useState<string | null>(null);

  useEffect(() => {
    setSearch('');
    setDebouncedSearch('');
    setSearchResults([]);
    setSearchError(null);
    setSearching(false);
  }, [activeItemId]);

  useEffect(() => {
    if (!activeItemId) return;
    const trimmed = search.trim();
    if (trimmed.length < 2) {
      setDebouncedSearch('');
      setSearchResults([]);
      setSearchError(null);
      setSearching(false);
      return;
    }
    setSearching(true);
    setSearchError(null);
    const handle = window.setTimeout(() => setDebouncedSearch(trimmed), 300);
    return () => window.clearTimeout(handle);
  }, [search, activeItemId]);

  useEffect(() => {
    if (!activeItemId || !debouncedSearch) return;
    let isMounted = true;
    fetchUsers({ search: debouncedSearch, limit: 8, role: 'TECHNICIAN' })
      .then((response) => {
        if (!isMounted) return;
        const onlyTechs = (response.data ?? []).filter(
          (u) => (u.typeKey ?? '').toUpperCase() === 'TECHNICIAN',
        );
        setSearchResults(onlyTechs);
        setSearchError(null);
      })
      .catch((error) => {
        if (!isMounted) return;
        const message = error instanceof Error ? error.message : 'Failed to search technicians.';
        setSearchResults([]);
        setSearchError(message);
      })
      .finally(() => {
        if (isMounted) setSearching(false);
      });
    return () => { isMounted = false; };
  }, [debouncedSearch, activeItemId]);

  const format12H = (date: Date | string | null): string => {
    if (!date) return '—';
    const d = new Date(date);
    if (isNaN(d.getTime())) return '—';
    return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: true });
  };

  const options: SearchableOption[] = useMemo(
    () => {
      // Only cross-order conflicts make a technician unavailable
      const crossOrderConflicts = currentTimeConflicts.filter(
        (c) => String(c.orderId) !== String(order?.id),
      );
      return searchResults.map((user) => {
        const conflict = crossOrderConflicts.find(c => String(c.technicianId) === String(user.id));
        return {
          value: user.id,
          label: getUserDisplayName(user) + (conflict ? ' (Busy)' : ''),
          description: conflict
            ? `Unavailable: Overlaps with ${conflict.orderNumber} (${format12H(conflict.startTime)} - ${format12H(conflict.endTime)})`
            : (user.email ?? user.username),
          className: conflict ? 'opacity-60 grayscale pointer-events-none' : '',
          disabled: !!conflict,
        };
      });
    },
    [searchResults, currentTimeConflicts, order?.id],
  );


  const conflictsCount = useMemo(() => {
    let count = 0;
    serviceItems.forEach((item) => {
      const a = assignments[item.id];
      if (!a?.technician || !a?.startTime || !a?.endTime) return;
      const techConflict = currentTimeConflicts.find(c => String(c.technicianId) === String(a.technician?.id));
      const bayConflict = a.bayId ? currentTimeConflicts.find(c => String(c.bayId) === String(a.bayId)) : null;
      if (techConflict || bayConflict) count++;
    });
    return count;
  }, [serviceItems, assignments, currentTimeConflicts]);

  const allowTechnicianEditing = Boolean(onChange) && !readOnly;
  const trimmedSearch = search.trim();
  const emptyMessage = trimmedSearch.length < 2
    ? 'Type at least two characters to search technicians.'
    : searchError ?? 'No technicians matched your search.';

  const formatDateTime = (value: string | null): string => {
    if (!value) return '—';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '—';
    return date.toLocaleString([], { dateStyle: 'short', timeStyle: 'short', hour12: true });
  };

  const formatToHHMM = (dateStr: string | null): string => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return '';
    const h = d.getHours().toString().padStart(2, '0');
    const m = d.getMinutes().toString().padStart(2, '0');
    return `${h}:${m}`;
  };

  const handleSelect = (userId: string) => {
    if (!activeItemId) return;
    const match = searchResults.find((user) => user.id === userId) || null;
    setPendingAssignment(prev => prev ? ({ ...prev, technician: match }) : null);
    setSearch(''); // Clear search upon selection to close dropdown
    setLocalError(null);
  };

  const handleClear = (orderItemId: string) => {
    if (!onChange) return;
    onChange(orderItemId, null);
    if (activeItemId === orderItemId) {
      setPendingAssignment(prev => prev ? ({ ...prev, technician: null, bayId: null }) : null);
    }
  };

  const handleCompletedChange = (orderItemId: string, completed: boolean) => {
    if (onCompletedChange) onCompletedChange(orderItemId, completed);
  };

  if (!serviceItems.length) {
    return (
      <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-500">
        No workshop management services were added to this order.
      </div>
    );
  }

  const showStatusColumns = allowStatusEditing && Boolean(onCompletedChange);
  const columnCount = 6 + (showStatusColumns ? 2 : 0) + (allowNextDueEditing ? 1 : 0) + (allowTechnicianEditing ? 1 : 0);

  return (
    <div className="space-y-4 my-2">
      <div className="flex items-center justify-between my-2">
        <p className="text-xs text-slate-500">
          Review technician assignments, completion status, and notes for each service.
        </p>
      </div>

      {conflictsCount > 0 && (
        <div className="mb-4 rounded-xl border border-red-300 bg-red-50 px-4 py-2 text-xs font-bold text-red-700 flex items-center gap-2">
          <svg className="w-4 h-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 17c-.77 1.333.192 3 1.732 3z" />
          </svg>
          Warning: Detected {conflictsCount} resource {conflictsCount === 1 ? 'conflict' : 'conflicts'} in this order.
        </div>
      )}

      <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-white shadow-sm">
        <table className="min-w-full divide-y divide-slate-200 text-sm">
          <thead className="bg-slate-50 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-4 py-3 min-w-[200px]">Service</th>
              <th className="px-4 py-3 min-w-[120px]">Bay</th>
              <th className="px-4 py-3 min-w-[160px]">Assigned technician</th>
              <th className="px-4 py-3 min-w-[140px]">Start Time</th>
              <th className="px-4 py-3 min-w-[140px]">EST End Time</th>
              <th className="px-4 py-3 min-w-[200px]">Notes</th>
              {showStatusColumns && <th className="px-4 py-3 min-w-[100px]">Completed</th>}
              {showStatusColumns && <th className="px-4 py-3 min-w-[140px]">Completed at</th>}
              {allowNextDueEditing && <th className="px-4 py-3 min-w-[140px]">Next due (days)</th>}
              {allowTechnicianEditing && <th className="px-4 py-3 text-right sticky right-0 bg-slate-50 z-10 w-40">Actions</th>}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-200 bg-white">
            {serviceItems.map((item) => {
              const baseAssignment = assignments[item.id] ?? {
                technician: null,
                completed: false,
                completedAt: null,
                notes: '',
                startTime: null,
                endTime: null,
              };
              const isActive = allowTechnicianEditing && activeItemId === item.id;
              const assignment = isActive && pendingAssignment ? pendingAssignment : baseAssignment;
              const assigned = assignment.technician;
              const completed = Boolean(baseAssignment.completed);
              const notes = assignment.notes ?? '';
              const currentBayId = String(assignment.bayId ?? '');
              const hasTechnician = Boolean(assigned);
              const currentNextDue = (nextDueByItem && nextDueByItem[item.id] !== undefined)
                ? nextDueByItem[item.id]
                : (typeof item.nextDueAfterDays === 'number' ? String(item.nextDueAfterDays) : '');

              return (
                <Fragment key={item.id}>
                  <tr className={`group transition-colors ${isActive ? 'bg-primary/5' : 'hover:bg-slate-50/50'}`}>
                    <td className="px-4 py-3 align-top">
                      <div className="flex flex-col gap-1">
                        <span className="font-medium text-slate-800">{getOrderItemDisplayName(item)}</span>
                        {item.productVariant?.erpId && (
                          <span className="text-xs text-slate-400">Variant ERP ID: {item.productVariant.erpId}</span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 align-top">
                      {bays.find((b) => String(b.id) === currentBayId)?.name || '—'}
                    </td>
                    <td className="px-4 py-3 align-top">
                      {assigned ? (
                        <span className="font-medium text-slate-700">{getUserDisplayName(assigned)}</span>
                      ) : (
                        <span className="text-xs text-slate-500">No technician assigned</span>
                      )}
                    </td>
                    <td className="px-4 py-3 align-top whitespace-nowrap">
                      <span className="text-xs text-slate-600">{formatDateTime(assignment.startTime)}</span>
                    </td>
                    <td className="px-4 py-3 align-top whitespace-nowrap">
                      <span className="text-xs text-slate-600">{formatDateTime(assignment.endTime)}</span>
                    </td>
                    <td className="px-4 py-3 align-top min-w-[200px]">
                      <span className="text-xs text-slate-500 whitespace-pre-wrap break-words">
                        {notes.trim() || '—'}
                      </span>
                    </td>
                    {showStatusColumns && (
                      <td className="px-4 py-3 align-top">
                        <label className="inline-flex items-center gap-2 text-xs font-medium text-slate-600">
                          <input
                            type="checkbox"
                            className="h-4 w-4 accent-primary"
                            checked={completed}
                            onChange={(event) => handleCompletedChange(item.id, event.target.checked)}
                            disabled={disabled || !showStatusColumns || !hasTechnician}
                          />
                          <span className="text-xs text-slate-500">{completed ? 'Completed' : 'Pending'}</span>
                        </label>
                      </td>
                    )}
                    {showStatusColumns && (
                      <td className="px-4 py-3 align-top">
                        <span className="text-xs text-slate-500">{formatDateTime(completed ? baseAssignment.completedAt : null)}</span>
                      </td>
                    )}
                    {allowNextDueEditing && (
                      <td className="px-4 py-3 align-top">
                        <input
                          type="number"
                          min={0}
                          step={1}
                          value={currentNextDue}
                          onChange={(e) => onNextDueChange?.(item.id, e.target.value.replace(/[^0-9]/g, ''))}
                          className="w-28 rounded-lg border border-slate-300 px-3 py-1.5 text-xs text-slate-700 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary/20 disabled:cursor-not-allowed disabled:opacity-60"
                          disabled={disabled || !hasTechnician}
                          placeholder="—"
                        />
                      </td>
                    )}
                    {allowTechnicianEditing && (
                      <td className="px-4 py-3 text-right align-top sticky right-0 bg-white group-hover:bg-slate-50 z-10">
                        <div className="flex justify-end gap-2">
                          <button
                            type="button"
                            className="rounded-lg border border-primary/30 px-3 py-1 text-xs font-semibold text-primary transition hover:border-primary hover:bg-primary/10 disabled:cursor-not-allowed disabled:opacity-60 whitespace-nowrap"
                            onClick={() => setActiveItemId((prev) => (prev === item.id ? null : item.id))}
                            disabled={disabled || completed}
                          >
                            {isActive ? 'Close Panel' : assigned ? 'Change' : 'Assign'}
                          </button>
                          <button
                            type="button"
                            className="rounded-lg border border-slate-300 px-3 py-1 text-xs font-semibold text-slate-600 transition hover:border-slate-400 hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-60"
                            onClick={() => handleClear(item.id)}
                            disabled={disabled || !assigned || completed}
                          >
                            Clear
                          </button>
                        </div>
                      </td>
                    )}
                  </tr>

                  {isActive && pendingAssignment && (
                    <tr>
                      <td colSpan={columnCount} className="px-6 py-6 bg-slate-50/50 border-b border-slate-100">
                        <div className="flex flex-col gap-6">
                          {currentTimeConflicts.length > 0 && (
                            <div className="mb-4 rounded-xl bg-orange-50 border border-orange-200 p-3 flex gap-3 items-start">
                              <div className="text-orange-500 mt-0.5">
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" /></svg>
                              </div>
                              <div>
                                <p className="text-xs font-bold text-orange-800">Overlap Detected</p>
                                <p className="text-[11px] text-orange-700 leading-relaxed mt-0.5">This time slot overlaps with other assignments:</p>
                                <ul className="mt-1.5 space-y-1">
                                  {currentTimeConflicts.map((c, i) => (
                                    <li key={i} className="text-[10px] text-orange-600 flex items-center gap-1.5">
                                      <span className="w-1 h-1 rounded-full bg-orange-400"></span>
                                      <span className="font-semibold">{c.orderNumber}</span> - {c.serviceName}
                                      <span className="opacity-70">({format12H(c.startTime)} - {format12H(c.endTime)})</span>
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            </div>
                          )}

                          <div className="flex flex-wrap items-center gap-8">
                            <div className="flex items-center gap-2">
                              <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Technician:</label>
                              <SearchableDropdown
                                options={options}
                                searchValue={search}
                                onSearchChange={setSearch}
                                selectedValue={pendingAssignment.technician?.id}
                                onSelect={handleSelect}
                                loading={searching}
                                emptyMessage={emptyMessage}
                                placeholder="Select technician"
                                className="w-64"
                              />
                            </div>
                            <div className="flex items-center gap-2">
                              <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Bay:</label>
                              <select
                                className="w-48 rounded-lg border border-slate-300 px-3 py-2 text-xs text-slate-700 h-[38px] font-medium focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all"
                                value={pendingAssignment.bayId ?? ''}
                                onChange={(e) => {
                                  const val = e.target.value;
                                  setPendingAssignment(prev => prev ? ({ ...prev, bayId: val || null }) : null);
                                  setLocalError(null);
                                }}
                                disabled={disabled || !pendingAssignment.technician}
                              >
                                <option value="">{!pendingAssignment.technician ? 'Select technician first' : 'Select bay'}</option>
                                {bays.filter(b => {
                                  const bayFullDayConflicts = dayConflicts.filter(c => String(c.bayId) === String(b.id));
                                  let totalBusyMins = 0;
                                  bayFullDayConflicts.forEach(c => {
                                    const start = new Date(Math.max(new Date(c.startTime).getTime(), new Date().setHours(10, 0, 0, 0)));
                                    const end = new Date(Math.min(new Date(c.endTime).getTime(), new Date().setHours(18, 0, 0, 0)));
                                    if (end > start) totalBusyMins += (end.getTime() - start.getTime()) / 60000;
                                  });
                                  return totalBusyMins < 480 || String(b.id) === String(pendingAssignment.bayId);
                                }).map((b) => {
                                  const isCurrentlyOccupied = currentTimeConflicts.some(c => String(c.bayId) === String(b.id));
                                  return (
                                    <option key={b.id} value={b.id} disabled={isCurrentlyOccupied}>
                                      {b.name} {isCurrentlyOccupied ? '(Occupied)' : ''}
                                    </option>
                                  );
                                })}
                              </select>
                            </div>
                          </div>

                          {/* ── Day availability timeline ── */}
                          {pendingAssignment.technician && (() => {
                            const DAY_START = 10 * 60; // 10:00 in minutes
                            const DAY_END = 18 * 60; // 18:00 in minutes
                            const DAY_MINS = DAY_END - DAY_START; // 480

                            const toMins = (iso: string) => {
                              const d = new Date(iso);
                              return d.getHours() * 60 + d.getMinutes();
                            };

                            const pct = (mins: number) =>
                              `${Math.min(100, Math.max(0, ((mins - DAY_START) / DAY_MINS) * 100)).toFixed(2)}%`;

                            const techSlots = dayConflicts.filter(
                              (c) => String(c.technicianId) === String(pendingAssignment.technician?.id),
                            );
                            // Split: same-order (indigo) vs external conflicts (red)
                            const sameOrderTechSlotsFromApi = techSlots.filter(c => String(c.orderId) === String(order?.id));
                            const otherOrderTechSlots = techSlots.filter(c => String(c.orderId) !== String(order?.id));

                            // Also include in-memory pending assignments from the same order
                            // (dayConflicts only returns saved DB records — unsaved pending slots are invisible otherwise)
                            const inMemorySameOrderSlots = Object.entries(assignments)
                              .filter(([itemId, a]) =>
                                itemId !== activeItemId &&
                                a.technician &&
                                String((a.technician as any).id) === String(pendingAssignment.technician?.id) &&
                                a.startTime && a.endTime,
                              )
                              .map(([itemId, a]) => ({
                                startTime: a.startTime!,
                                endTime: a.endTime!,
                                orderId: String(order?.id),
                                serviceName: (() => { const si = serviceItems.find(si => si.id === itemId); return si ? getOrderItemDisplayName(si) : 'Other service'; })(),
                                orderNumber: (order as any)?.orderNumber ?? '',
                              }));

                            // Merge, de-duplicating by startTime+endTime
                            const sameOrderTechSlots = [...sameOrderTechSlotsFromApi];
                            for (const slot of inMemorySameOrderSlots) {
                              const duplicate = sameOrderTechSlots.some(
                                s => s.startTime === slot.startTime && s.endTime === slot.endTime,
                              );
                              if (!duplicate) sameOrderTechSlots.push(slot);
                            }

                            // pending window — flag conflict only for OTHER-order overlaps
                            const pendStart = pendingAssignment.startTime ? toMins(pendingAssignment.startTime) : null;
                            const pendEnd = pendingAssignment.endTime ? toMins(pendingAssignment.endTime) : null;
                            const pendConflicts = pendStart !== null && pendEnd !== null
                              ? otherOrderTechSlots.filter(c => {
                                const s = toMins(c.startTime);
                                const e = toMins(c.endTime);
                                return s < pendEnd! && e > pendStart!;
                              })
                              : [];

                            const hourTicks = [10, 11, 12, 13, 14, 15, 16, 17, 18];

                            return (
                              <div className="border-t border-slate-100 pt-4 pb-1">
                                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-2">
                                  {pendingAssignment.technician
                                    ? `${(pendingAssignment.technician as any).firstName ?? ''} ${(pendingAssignment.technician as any).lastName ?? ''}`.trim() || 'Technician'
                                    : 'Technician'
                                  }'s availability today
                                </p>

                                {/* Timeline bar */}
                                <div className="relative h-7 w-full rounded-lg bg-emerald-50 border border-emerald-100 overflow-hidden">
                                  {/* Hour tick marks */}
                                  {hourTicks.map(h => (
                                    <div
                                      key={h}
                                      className="absolute top-0 bottom-0 w-px bg-slate-200/60"
                                      style={{ left: pct(h * 60) }}
                                    />
                                  ))}

                                  {/* External conflict blocks (red) */}
                                  {otherOrderTechSlots.map((c, i) => {
                                    const s = Math.max(toMins(c.startTime), DAY_START);
                                    const e = Math.min(toMins(c.endTime), DAY_END);
                                    if (e <= s) return null;
                                    const left = pct(s);
                                    const width = `${(((e - s) / DAY_MINS) * 100).toFixed(2)}%`;
                                    return (
                                      <div
                                        key={`ext-${i}`}
                                        className="absolute top-0 bottom-0 bg-red-400/80 flex items-center justify-center"
                                        style={{ left, width }}
                                        title={`${c.orderNumber} (${format12H(c.startTime)} – ${format12H(c.endTime)})`}
                                      >
                                        <span className="text-[8px] text-white font-bold truncate px-0.5 leading-none">
                                          {c.orderNumber}
                                        </span>
                                      </div>
                                    );
                                  })}

                                  {/* Same-order blocks (indigo) */}
                                  {sameOrderTechSlots.map((c, i) => {
                                    const s = Math.max(toMins(c.startTime), DAY_START);
                                    const e = Math.min(toMins(c.endTime), DAY_END);
                                    if (e <= s) return null;
                                    const left = pct(s);
                                    const width = `${(((e - s) / DAY_MINS) * 100).toFixed(2)}%`;
                                    return (
                                      <div
                                        key={`own-${i}`}
                                        className="absolute top-0 bottom-0 flex items-center justify-center"
                                        style={{ left, width, backgroundColor: '#6366f1' }}
                                        title={`This order – ${c.serviceName ?? c.orderNumber} (${format12H(c.startTime)} – ${format12H(c.endTime)})`}
                                      >
                                        <span className="text-[8px] text-white font-bold truncate px-0.5 leading-none">
                                          {c.serviceName ?? 'This order'}
                                        </span>
                                      </div>
                                    );
                                  })}

                                  {/* Current pending selection */}
                                  {pendStart !== null && pendEnd !== null && pendEnd > pendStart && (
                                    <div
                                      className={`absolute top-0 bottom-0 border-2 rounded ${pendConflicts.length > 0 ? 'bg-orange-300/50 border-orange-400' : 'bg-primary/30 border-primary'}`}
                                      style={{
                                        left: pct(Math.max(pendStart, DAY_START)),
                                        width: `${((Math.min(pendEnd, DAY_END) - Math.max(pendStart, DAY_START)) / DAY_MINS * 100).toFixed(2)}%`,
                                      }}
                                    />
                                  )}
                                </div>

                                {/* Hour labels */}
                                <div className="relative mt-0.5 h-3">
                                  {hourTicks.map(h => (
                                    <span
                                      key={h}
                                      className="absolute text-[8px] text-slate-400 -translate-x-1/2"
                                      style={{ left: pct(h * 60) }}
                                    >
                                      {h < 12 ? `${h}A` : h === 12 ? '12P' : `${h - 12}P`}
                                    </span>
                                  ))}
                                </div>

                                <div className="flex flex-wrap items-center gap-3 mt-2">
                                  <span className="flex items-center gap-1 text-[10px] text-slate-500">
                                    <span className="inline-block w-2.5 h-2.5 rounded-sm bg-red-400" /> Conflict
                                  </span>
                                  <span className="flex items-center gap-1 text-[10px] text-slate-500">
                                    <span className="inline-block w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: '#6366f1' }} /> This order
                                  </span>
                                  <span className="flex items-center gap-1 text-[10px] text-slate-500">
                                    <span className="inline-block w-2.5 h-2.5 rounded-sm bg-emerald-100 border border-emerald-200" /> Free
                                  </span>
                                  {pendStart !== null && (
                                    <span className="flex items-center gap-1 text-[10px] text-slate-500">
                                      <span className={`inline-block w-2.5 h-2.5 rounded-sm border-2 ${pendConflicts.length > 0 ? 'bg-orange-300/50 border-orange-400' : 'bg-primary/30 border-primary'}`} />
                                      {pendConflicts.length > 0 ? 'Selected (conflict!)' : 'Selected slot'}
                                    </span>
                                  )}
                                </div>

                                {techSlots.length === 0 && (
                                  <p className="text-[10px] text-emerald-600 font-semibold mt-1">✓ Fully free today</p>
                                )}

                                {techSlots.length > 0 && (
                                  <ul className="mt-1.5 space-y-0.5">
                                    {otherOrderTechSlots.map((c, i) => (
                                      <li key={`ext-${i}`} className="flex items-center gap-2 text-[10px] text-slate-600">
                                        <span className="w-1.5 h-1.5 rounded-full bg-red-400 shrink-0" />
                                        <span className="font-semibold text-slate-700">{format12H(c.startTime)} – {format12H(c.endTime)}</span>
                                        <span className="text-slate-400">·</span>
                                        <span className="truncate">{c.orderNumber}{c.serviceName ? ` · ${c.serviceName}` : ''}</span>
                                      </li>
                                    ))}
                                    {sameOrderTechSlots.map((c, i) => (
                                      <li key={`own-${i}`} className="flex items-center gap-2 text-[10px] text-slate-600">
                                        <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ backgroundColor: '#6366f1' }} />
                                        <span className="font-semibold" style={{ color: '#6366f1' }}>{format12H(c.startTime)} – {format12H(c.endTime)}</span>
                                        <span className="text-slate-400">·</span>
                                        <span className="truncate" style={{ color: '#6366f1' }}>{c.serviceName ?? 'This order'}</span>
                                      </li>
                                    ))}
                                  </ul>
                                )}
                              </div>
                            );
                          })()}

                          {/* ── Bay availability timeline ── */}
                          {pendingAssignment.bayId && (() => {
                            const DAY_START = 10 * 60;
                            const DAY_END = 18 * 60;
                            const DAY_MINS = DAY_END - DAY_START;

                            const toMins = (iso: string) => {
                              const d = new Date(iso);
                              return d.getHours() * 60 + d.getMinutes();
                            };
                            const pct = (mins: number) =>
                              `${Math.min(100, Math.max(0, ((mins - DAY_START) / DAY_MINS) * 100)).toFixed(2)}%`;

                            const baySlots = bayDayConflicts.filter(
                              (c) => String(c.bayId) === String(pendingAssignment.bayId),
                            );
                            // Split: same-order (indigo) vs external (amber)
                            const sameOrderBaySlotsFromApi = baySlots.filter(c => String(c.orderId) === String(order?.id));
                            const otherOrderBaySlots = baySlots.filter(c => String(c.orderId) !== String(order?.id));

                            // In-memory same-order bay slots (unsaved assignments)
                            const inMemoryBaySlots = Object.entries(assignments)
                              .filter(([itemId, a]) =>
                                itemId !== activeItemId &&
                                String(a.bayId) === String(pendingAssignment.bayId) &&
                                a.startTime && a.endTime,
                              )
                              .map(([itemId, a]) => ({
                                startTime: a.startTime!,
                                endTime: a.endTime!,
                                orderId: String(order?.id),
                                serviceName: (() => { const si = serviceItems.find(si => si.id === itemId); return si ? getOrderItemDisplayName(si) : 'Other service'; })(),
                                orderNumber: (order as any)?.orderNumber ?? '',
                              }));

                            const sameOrderBaySlots = [...sameOrderBaySlotsFromApi];
                            for (const slot of inMemoryBaySlots) {
                              const dup = sameOrderBaySlots.some(
                                s => s.startTime === slot.startTime && s.endTime === slot.endTime,
                              );
                              if (!dup) sameOrderBaySlots.push(slot);
                            }

                            // Pending window — only flag OTHER-order overlaps
                            const pendStart = pendingAssignment.startTime ? toMins(pendingAssignment.startTime) : null;
                            const pendEnd = pendingAssignment.endTime ? toMins(pendingAssignment.endTime) : null;
                            const pendConflicts = pendStart !== null && pendEnd !== null
                              ? otherOrderBaySlots.filter(c => {
                                const s = toMins(c.startTime);
                                const e = toMins(c.endTime);
                                return s < pendEnd! && e > pendStart!;
                              })
                              : [];

                            const bayName = bays.find(b => String(b.id) === String(pendingAssignment.bayId))?.name ?? 'Bay';
                            const hourTicks = [10, 11, 12, 13, 14, 15, 16, 17, 18];

                            return (
                              <div className="border-t border-slate-100 pt-4 pb-1">
                                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-2">
                                  {bayName}'s occupancy today
                                </p>

                                {/* Timeline bar */}
                                <div className="relative h-7 w-full rounded-lg bg-amber-50 border border-amber-100 overflow-hidden">
                                  {hourTicks.map(h => (
                                    <div
                                      key={h}
                                      className="absolute top-0 bottom-0 w-px bg-slate-200/60"
                                      style={{ left: pct(h * 60) }}
                                    />
                                  ))}

                                  {/* External bay conflict blocks (amber) */}
                                  {otherOrderBaySlots.map((c, i) => {
                                    const s = Math.max(toMins(c.startTime), DAY_START);
                                    const e = Math.min(toMins(c.endTime), DAY_END);
                                    if (e <= s) return null;
                                    return (
                                      <div
                                        key={`ext-${i}`}
                                        className="absolute top-0 bottom-0 bg-amber-500/80 flex items-center justify-center"
                                        style={{ left: pct(s), width: `${(((e - s) / DAY_MINS) * 100).toFixed(2)}%` }}
                                        title={`${c.orderNumber} (${format12H(c.startTime)} – ${format12H(c.endTime)})`}
                                      >
                                        <span className="text-[8px] text-white font-bold truncate px-0.5 leading-none">
                                          {c.orderNumber}
                                        </span>
                                      </div>
                                    );
                                  })}

                                  {/* Same-order bay blocks (indigo) */}
                                  {sameOrderBaySlots.map((c, i) => {
                                    const s = Math.max(toMins(c.startTime), DAY_START);
                                    const e = Math.min(toMins(c.endTime), DAY_END);
                                    if (e <= s) return null;
                                    return (
                                      <div
                                        key={`own-${i}`}
                                        className="absolute top-0 bottom-0 flex items-center justify-center"
                                        style={{ left: pct(s), width: `${(((e - s) / DAY_MINS) * 100).toFixed(2)}%`, backgroundColor: '#6366f1' }}
                                        title={`This order – ${c.serviceName ?? c.orderNumber} (${format12H(c.startTime)} – ${format12H(c.endTime)})`}
                                      >
                                        <span className="text-[8px] text-white font-bold truncate px-0.5 leading-none">
                                          {c.serviceName ?? 'This order'}
                                        </span>
                                      </div>
                                    );
                                  })}

                                  {/* Current pending selection */}
                                  {pendStart !== null && pendEnd !== null && pendEnd > pendStart && (
                                    <div
                                      className={`absolute top-0 bottom-0 border-2 rounded ${pendConflicts.length > 0 ? 'bg-red-300/50 border-red-400' : 'bg-primary/30 border-primary'}`}
                                      style={{
                                        left: pct(Math.max(pendStart, DAY_START)),
                                        width: `${((Math.min(pendEnd, DAY_END) - Math.max(pendStart, DAY_START)) / DAY_MINS * 100).toFixed(2)}%`,
                                      }}
                                    />
                                  )}
                                </div>

                                {/* Hour labels */}
                                <div className="relative mt-0.5 h-3">
                                  {hourTicks.map(h => (
                                    <span
                                      key={h}
                                      className="absolute text-[8px] text-slate-400 -translate-x-1/2"
                                      style={{ left: pct(h * 60) }}
                                    >
                                      {h < 12 ? `${h}A` : h === 12 ? '12P' : `${h - 12}P`}
                                    </span>
                                  ))}
                                </div>

                                {/* Legend */}
                                <div className="flex flex-wrap items-center gap-3 mt-2">
                                  <span className="flex items-center gap-1 text-[10px] text-slate-500">
                                    <span className="inline-block w-2.5 h-2.5 rounded-sm bg-amber-500" /> Conflict
                                  </span>
                                  <span className="flex items-center gap-1 text-[10px] text-slate-500">
                                    <span className="inline-block w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: '#6366f1' }} /> This order
                                  </span>
                                  <span className="flex items-center gap-1 text-[10px] text-slate-500">
                                    <span className="inline-block w-2.5 h-2.5 rounded-sm bg-amber-50 border border-amber-200" /> Free
                                  </span>
                                  {pendStart !== null && (
                                    <span className="flex items-center gap-1 text-[10px] text-slate-500">
                                      <span className={`inline-block w-2.5 h-2.5 rounded-sm border-2 ${pendConflicts.length > 0 ? 'bg-red-300/50 border-red-400' : 'bg-primary/30 border-primary'}`} />
                                      {pendConflicts.length > 0 ? 'Selected (conflict!)' : 'Selected slot'}
                                    </span>
                                  )}
                                </div>

                                {baySlots.length === 0 && sameOrderBaySlots.length === 0 && (
                                  <p className="text-[10px] text-emerald-600 font-semibold mt-1">✓ Bay fully free today</p>
                                )}
                                {(otherOrderBaySlots.length > 0 || sameOrderBaySlots.length > 0) && (
                                  <ul className="mt-1.5 space-y-0.5">
                                    {otherOrderBaySlots.map((c, i) => (
                                      <li key={`ext-${i}`} className="flex items-center gap-2 text-[10px] text-slate-600">
                                        <span className="w-1.5 h-1.5 rounded-full bg-amber-500 shrink-0" />
                                        <span className="font-semibold text-slate-700">{format12H(c.startTime)} – {format12H(c.endTime)}</span>
                                        <span className="text-slate-400">·</span>
                                        <span className="truncate">{c.orderNumber}{c.serviceName ? ` · ${c.serviceName}` : ''}</span>
                                      </li>
                                    ))}
                                    {sameOrderBaySlots.map((c, i) => (
                                      <li key={`own-${i}`} className="flex items-center gap-2 text-[10px] text-slate-600">
                                        <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ backgroundColor: '#6366f1' }} />
                                        <span className="font-semibold" style={{ color: '#6366f1' }}>{format12H(c.startTime)} – {format12H(c.endTime)}</span>
                                        <span className="text-slate-400">·</span>
                                        <span className="truncate" style={{ color: '#6366f1' }}>{c.serviceName ?? 'This order'}</span>
                                      </li>
                                    ))}
                                  </ul>
                                )}
                              </div>
                            );
                          })()}

                          {/* ── Smart Free-Slot Suggestions ── */}
                          {(pendingAssignment.technician || pendingAssignment.bayId) && (() => {
                            const DAY_START_MIN = 10 * 60;
                            const DAY_END_MIN = 18 * 60;

                            const toMins = (iso: string) => {
                              const d = new Date(iso);
                              return d.getHours() * 60 + d.getMinutes();
                            };

                            // Gather all busy intervals for selected technician + bay
                            const busyIntervals: { s: number; e: number }[] = [];

                            if (pendingAssignment.technician) {
                              dayConflicts
                                .filter(c => String(c.technicianId) === String(pendingAssignment.technician?.id))
                                .forEach(c => busyIntervals.push({ s: toMins(c.startTime), e: toMins(c.endTime) }));
                            }
                            if (pendingAssignment.bayId) {
                              bayDayConflicts
                                .filter(c => String(c.bayId) === String(pendingAssignment.bayId))
                                .forEach(c => busyIntervals.push({ s: toMins(c.startTime), e: toMins(c.endTime) }));
                            }

                            // Merge overlapping intervals
                            busyIntervals.sort((a, b) => a.s - b.s);
                            const merged: { s: number; e: number }[] = [];
                            for (const iv of busyIntervals) {
                              if (merged.length === 0 || iv.s >= merged[merged.length - 1].e) {
                                merged.push({ ...iv });
                              } else {
                                merged[merged.length - 1].e = Math.max(merged[merged.length - 1].e, iv.e);
                              }
                            }

                            // Find free gaps ≥ 15 minutes
                            const freeSlots: { startMin: number; endMin: number }[] = [];
                            let cursor = DAY_START_MIN;
                            for (const iv of merged) {
                              const from = Math.max(iv.s, DAY_START_MIN);
                              const to = Math.min(iv.e, DAY_END_MIN);
                              if (from > cursor && from - cursor >= 15) {
                                freeSlots.push({ startMin: cursor, endMin: from });
                              }
                              cursor = Math.max(cursor, to);
                            }
                            if (DAY_END_MIN - cursor >= 15) {
                              freeSlots.push({ startMin: cursor, endMin: DAY_END_MIN });
                            }

                            const minToHHMM = (m: number) => {
                              const h = Math.floor(m / 60);
                              const min = m % 60;
                              return `${String(h).padStart(2, '0')}:${String(min).padStart(2, '0')}`;
                            };
                            const minTo12H = (m: number) => {
                              const h = Math.floor(m / 60);
                              const min = m % 60;
                              const ampm = h < 12 ? 'AM' : 'PM';
                              const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
                              return `${h12}:${String(min).padStart(2, '0')} ${ampm}`;
                            };

                            // Clicking a suggestion sets BOTH start and end directly from the suggested window.
                            // We do NOT preserve the manual duration — the suggestion defines the booking slot.
                            const handleSlotClick = (startMin: number, endMin: number) => {
                              const base = pendingAssignment.startTime
                                ? new Date(pendingAssignment.startTime)
                                : new Date();
                              const start = new Date(base);
                              start.setHours(Math.floor(startMin / 60), startMin % 60, 0, 0);
                              const end = new Date(base);
                              end.setHours(Math.floor(endMin / 60), endMin % 60, 0, 0);
                              setPendingAssignment(prev => prev ? ({ ...prev, startTime: start.toISOString(), endTime: end.toISOString() }) : null);
                            };

                            return (
                              <div className="border-t border-slate-100 pt-3 pb-2">
                                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-2 flex items-center gap-1.5">
                                  <span>✨</span> Suggested free slots
                                  <span className="font-normal normal-case text-slate-400">
                                    {pendingAssignment.technician && pendingAssignment.bayId
                                      ? '(both technician & bay free)'
                                      : pendingAssignment.technician ? '(technician free)' : '(bay free)'}
                                  </span>
                                </p>

                                {freeSlots.length === 0 ? (
                                  <p className="text-[11px] text-red-500 font-semibold">
                                    ⚠ No free slots found today for this combination.
                                  </p>
                                ) : (
                                  <div className="flex flex-wrap gap-1.5">
                                    {freeSlots.map((slot, i) => (
                                      <button
                                        key={i}
                                        type="button"
                                        onClick={() => handleSlotClick(slot.startMin, slot.endMin)}
                                        className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg border border-emerald-200 bg-emerald-50 hover:bg-emerald-100 hover:border-emerald-400 text-emerald-700 text-[10px] font-semibold transition-all cursor-pointer"
                                        title={`Click to apply ${minTo12H(slot.startMin)} – ${minTo12H(slot.endMin)}`}
                                      >
                                        <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 shrink-0" />
                                        {minTo12H(slot.startMin)}
                                        <span className="text-emerald-400 font-normal">–</span>
                                        {minTo12H(slot.endMin)}
                                      </button>
                                    ))}
                                  </div>
                                )}
                              </div>
                            );
                          })()}

                          <div className={`flex flex-wrap items-center gap-4 border-t border-slate-100 pt-3 ${!pendingAssignment.bayId ? 'opacity-50 pointer-events-none' : ''}`}>
                            <label className="text-xs font-bold text-slate-500 uppercase tracking-wider w-24">Schedule:</label>
                            <div className="flex flex-wrap items-center gap-3">
                              <div className="flex flex-col gap-1">
                                <span className="text-[10px] text-slate-400 font-bold uppercase ml-1">Start Time</span>
                                <input
                                  type="time"
                                  min="10:00"
                                  max="18:00"
                                  className="w-32 rounded-xl border border-slate-200 px-3 py-2 text-xs text-slate-700 h-[38px] focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all font-medium"
                                  value={formatToHHMM(pendingAssignment.startTime)}
                                  onChange={(e) => {
                                    const val = e.target.value;
                                    if (val < "10:00" || val > "18:00") {
                                      setLocalError('Start time must be between 10 AM and 6 PM.');
                                    } else {
                                      setLocalError(null);
                                    }
                                    const [h, m] = val.split(':');
                                    const d = pendingAssignment.startTime ? new Date(pendingAssignment.startTime) : new Date();
                                    d.setHours(parseInt(h), parseInt(m), 0, 0);

                                    // Keep existing duration or default to 30m
                                    const currentStart = pendingAssignment.startTime ? new Date(pendingAssignment.startTime) : null;
                                    const currentEnd = pendingAssignment.endTime ? new Date(pendingAssignment.endTime) : null;
                                    let durationMs = 30 * 60000;
                                    if (currentStart && currentEnd && currentEnd > currentStart) {
                                      durationMs = currentEnd.getTime() - currentStart.getTime();
                                    }

                                    const newEnd = new Date(d.getTime() + durationMs);
                                    setPendingAssignment(prev => prev ? ({ ...prev, startTime: d.toISOString(), endTime: newEnd.toISOString() }) : null);
                                  }}
                                />
                              </div>
                              <div className="flex flex-col gap-1">
                                <span className="text-[10px] text-slate-400 font-bold uppercase ml-1">Duration</span>
                                <select
                                  className="w-28 rounded-xl border border-slate-200 px-3 py-2 text-xs text-slate-700 h-[38px] focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all font-medium"
                                  value={(() => {
                                    const start = pendingAssignment.startTime ? new Date(pendingAssignment.startTime) : null;
                                    const end = pendingAssignment.endTime ? new Date(pendingAssignment.endTime) : null;
                                    if (!start || !end || end <= start) return '30';
                                    return String((end.getTime() - start.getTime()) / 60000);
                                  })()}
                                  onChange={(e) => {
                                    const mins = parseInt(e.target.value);
                                    const start = pendingAssignment.startTime ? new Date(pendingAssignment.startTime) : new Date();
                                    const end = new Date(start.getTime() + mins * 60000);
                                    setPendingAssignment(prev => prev ? ({ ...prev, endTime: end.toISOString() }) : null);
                                    setLocalError(null);
                                  }}
                                >
                                  <option value="12">12 min</option>
                                  <option value="15">15 min</option>
                                  <option value="30">30 min</option>
                                  <option value="45">45 min</option>
                                  <option value="60">1 hour</option>
                                  <option value="90">1.5 hours</option>
                                  <option value="120">2 hours</option>
                                  <option value="180">3 hours</option>
                                  <option value="240">4 hours</option>
                                </select>
                              </div>
                              <div className="flex flex-col gap-1">
                                <span className="text-[10px] text-slate-400 font-bold uppercase ml-1 tracking-tighter text-nowrap">EST End Time</span>
                                <input
                                  type="time"
                                  min="10:00"
                                  max="18:00"
                                  className="w-32 rounded-xl border border-slate-200 px-3 py-2 text-xs text-slate-700 h-[38px] focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all font-medium"
                                  value={formatToHHMM(pendingAssignment.endTime)}
                                  onChange={(e) => {
                                    const val = e.target.value;
                                    if (val < "10:00" || val > "18:00") {
                                      setLocalError('End time must be between 10 AM and 6 PM.');
                                    } else {
                                      setLocalError(null);
                                    }
                                    const [h, m] = val.split(':');
                                    const d = pendingAssignment.endTime ? new Date(pendingAssignment.endTime) : (pendingAssignment.startTime ? new Date(pendingAssignment.startTime) : new Date());
                                    d.setHours(parseInt(h), parseInt(m), 0, 0);
                                    setPendingAssignment(prev => prev ? ({ ...prev, endTime: d.toISOString() }) : null);
                                  }}
                                />
                              </div>
                            </div>
                            <div className="flex flex-col gap-1 w-48">
                              <span className="text-[10px] text-slate-400 font-bold uppercase ml-1">Notes</span>
                              <input
                                type="text"
                                placeholder="Note..."
                                className="w-full rounded-xl border border-slate-200 px-4 py-2 text-xs text-slate-700 h-[38px] focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none"
                                value={pendingAssignment.notes ?? ''}
                                onChange={(e) => {
                                  const val = e.target.value;
                                  setPendingAssignment(prev => prev ? ({ ...prev, notes: val }) : null);
                                }}
                              />
                            </div>
                            <div className="flex flex-col gap-1">
                              <button
                                type="button"
                                className="self-end rounded-xl bg-primary px-6 py-2 text-xs font-bold text-white shadow-sm hover:bg-primary-dark transition-all h-[38px] active:scale-95 disabled:opacity-50"
                                onClick={() => {
                                  if (!pendingAssignment) return;
                                  const start = pendingAssignment.startTime ? new Date(pendingAssignment.startTime) : null;
                                  const end = pendingAssignment.endTime ? new Date(pendingAssignment.endTime) : null;
                                  if (!pendingAssignment.technician) { setLocalError('Please select a technician.'); return; }
                                  if (!pendingAssignment.bayId) { setLocalError('Please select a bay.'); return; }
                                  if (!start || !end) { setLocalError('Please select start and end times.'); return; }
                                  if (end <= start) { setLocalError('End time must be after start time.'); return; }
                                  const techConflict = currentTimeConflicts.find(c => String(c.technicianId) === String(pendingAssignment.technician?.id));
                                  const bayConflict = currentTimeConflicts.find(c => String(c.bayId) === String(pendingAssignment.bayId));
                                  if (techConflict) { setLocalError(`Technician busy: Already assigned to ${techConflict.orderNumber} ${format12H(techConflict.startTime)}-${format12H(techConflict.endTime)}.`); return; }
                                  if (bayConflict) { setLocalError(`Bay occupied: Already in use for ${bayConflict.orderNumber} ${format12H(bayConflict.startTime)}-${format12H(bayConflict.endTime)}.`); return; }

                                  const startHHMM = formatToHHMM(pendingAssignment.startTime);
                                  const endHHMM = formatToHHMM(pendingAssignment.endTime);
                                  if (startHHMM < "10:00" || startHHMM > "18:00" || endHHMM < "10:00" || endHHMM > "18:00") {
                                    setLocalError('Operating hours are 10:00 AM - 06:00 PM.');
                                    return;
                                  }
                                  onChange?.(item.id, pendingAssignment.technician);
                                  onBayChange?.(item.id, pendingAssignment.bayId || null);
                                  onStartTimeChange?.(item.id, pendingAssignment.startTime);
                                  onEndTimeChange?.(item.id, pendingAssignment.endTime);
                                  onNotesChange?.(item.id, pendingAssignment.notes);
                                  setActiveItemId(null);
                                }}
                              >
                                Confirm
                              </button>
                            </div>
                          </div>

                          {localError && (
                            <div className="px-4 py-2 bg-red-50 border border-red-100 rounded-xl text-red-600 text-[10px] font-bold uppercase animate-pulse flex items-center gap-2">
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" /></svg>
                              {localError}
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ServiceAssignmentsTable;
